// RichTextEditor.jsx
import { useMemo } from "react";
import { Editor } from "react-draft-wysiwyg";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { FormHelperText, FormLabel, styled } from "@mui/material";
import htmlToDraft from "html-to-draftjs";
import { ContentState, EditorState } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import translations from "configs/locales/el/richTextEditor";
import { NormalText } from "components";
import InsertPlaceholderDropdown from "./InsertPlaceholderDropdown";

const ContainerStyled = styled("div")({
  "& .label": {
    marginLeft: "0.5rem",
  },
  "& .editor-wrapper": {
    borderRadius: "0.5rem",
    padding: "0px",
    backgroundColor: "#f8f8f8",
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
    color: "#6f6f6f",
    overflow: "hidden",
  },
  "& .editor-editor": {
    minHeight: "200px",
    border: "1px solid #dcdcdc",
    padding: "10px",
    borderRadius: "0",
    backgroundColor: "#ffffff",
    fontFamily: '"Verdana", sans-serif',
    fontSize: "inherit",
    "&:focus": {
      outline: "none",
      borderColor: "#a0a0a0",
    },
  },
  "& .editor-toolbar": {
    border: "0",
    borderRadius: "0",
    padding: "0",
    display: "flex",
    flexWrap: "wrap",
    gap: "1rem",
    alignItems: "center",
    backgroundColor: "#d2d9e9",
    color: "#6f6f6f",
    margin: "0",
    transition: "background-color 0.2s",
    "&.only-insert-options": {
      justifyContent: "flex-end",
    },
    "& .rdw-inline-wrapper": {
      marginBottom: "0",
      fontSize: "20px",
      color: "#6f6f6f",
      padding: "5px",
    },
    "& .rdw-fontfamily-wrapper": {
      backgroundColor: "#d2d9e9",
      marginBottom: 0,
      "& .rdw-dropdown-selectedtext": {
        paddingLeft: "1rem",
      },
    },
    "& .rdw-fontsize-wrapper": {
      marginBottom: 0,
    },
    "& .rdw-dropdown-wrapper": {
      border: "none",
      borderRadius: "3px",
      "&:hover": {
        boxShadow: "none",
      },
    },
    "& .rdw-dropdown-selectedtext": {
      backgroundColor: "#d2d9e9",
      transition: "background-color 0.2s",
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: "#e8e8e8",
      },
    },
    "& .rdw-dropdown-carettoopen, .rdw-dropdown-carettoclose": {
      borderTop: "6px solid #6f6f6f",
    },
    "& .rdw-option-wrapper": {
      border: "none",
      padding: "15px 5px",
      margin: "0 2px",
      borderRadius: "3px",
      fontSize: "20px",
      backgroundColor: "#d2d9e9",
      transition: "background-color 0.2s",
      "&:hover": {
        backgroundColor: "#e8e8e8",
        boxShadow: "none",
      },
      "& img": {
        opacity: "0.5",
      },
    },
  },
  "& .DraftEditor-editorContainer *": {
    fontFamily: "inherit",
  },
  "& .custom-font-dropdown, .custom-font-size-dropdown, .custom-align-dropdown":
    {
      borderRadius: "3px",
      boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.15)",
    },
  "& .font-family-label.greek, .custom-font-dropdown.greek": {
    width: "160px",
  },
  "& .font-family-label.greek": {
    "& .rdw-fontfamily-placeholder": {
      maxWidth: "160px",
    },
  },
  "&.view-mode": {
    "& .helper": {
      display: "none",
    },
    "& .editor-wrapper": {
      boxShadow: "none",
      backgroundColor: "transparent",
    },
    "& .editor-toolbar": {
      display: "none",
    },
    "& .editor-editor": {
      border: "none",
      backgroundColor: "inherit",
      "& .public-DraftEditorPlaceholder-root": {
        display: "none",
      },
    },
  },
});

const MaxCharsLimit = styled(NormalText)(({ theme }) => ({
  marginTop: "0.5rem",
  marginRight: "0.5rem",
  float: "right",
  color: "#4D4D4D",
  "&.limit-reached": {
    color: theme.palette.error.main,
  },
}));

const InsertPlaceholderDropdownFloatEnd = styled(InsertPlaceholderDropdown)({
  flex: 1,
  display: "flex",
  justifyContent: "flex-end",
});

const RichTextEditor = ({
  maxChars,
  charCount,
  onChange,
  value,
  toolbar = {},
  helperText,
  isError,
  label,
  isViewMode,
  type,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const isGreek = i18n.language.startsWith("el");

  const editorState = useMemo(() => {
    if (isViewMode) {
      let contentBlock = value;

      const shouldConvertHtml = type === "email" && typeof value === "string";

      if (shouldConvertHtml) {
        contentBlock = htmlToDraft(value);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks
          );
          const finalEditorState = EditorState.createWithContent(contentState);
          return finalEditorState;
        }
      }
    }
    return value;
  }, [isViewMode, type, value]);

  const isLimitExceeded = charCount > maxChars;

  return (
    <ContainerStyled className={clsx({ "view-mode": isViewMode }, className)}>
      <FormLabel className="label">{label}</FormLabel>
      <Editor
        editorState={editorState}
        readOnly={isViewMode}
        onEditorStateChange={onChange}
        wrapperClassName="editor-wrapper"
        editorClassName="editor-editor"
        toolbarClassName={clsx("editor-toolbar", {
          "only-insert-options": !toolbar?.options?.length,
        })}
        toolbar={{
          ...toolbar,
          fontFamily: {
            ...toolbar?.fontFamily,
            className: clsx("font-family-label", { greek: isGreek }),
            dropdownClassName: clsx(toolbar?.fontFamily?.dropdownClassName, {
              greek: isGreek,
            }),
          },
        }}
        toolbarCustomButtons={[
          <InsertPlaceholderDropdownFloatEnd
            key="insertPlaceholderDropdown"
            editorState={value}
            onChange={onChange}
          />,
        ]}
        localization={
          isGreek
            ? {
                translations,
              }
            : undefined
        }
        placeholder={t("RichTextEditor.Placeholder")}
      />
      {Boolean(maxChars) && (
        <MaxCharsLimit className={clsx({ "limit-reached": isLimitExceeded })}>
          {charCount}/{maxChars}
        </MaxCharsLimit>
      )}
      <FormHelperText error={isError} className="helper">
        {helperText}
      </FormHelperText>
    </ContainerStyled>
  );
};

export default RichTextEditor;
